<template>
  <ElDialog
    append-to-body
    destroy-on-close
    class="mail-service-page-edit-modal"
    :visible="value"
    width="830px"
    @close="closeAndReset"
  >
    <div slot="title" class="mail-service-page-edit-modal__header">
      {{ $t('ADVERTISEMENT.MAIL_SERVICE.EDIT_NOTIFICATION_TITLE') }}
      <p class="mail-service-page-edit-modal__header-desc">
        {{ $t('ADVERTISEMENT.MAIL_SERVICE.NIGHTLY_NOTIFICATIONS_DESCRIPTION') }}
      </p>
    </div>
    <ElForm
      :ref="formRef"
      label-position="left"
      :model="formModel"
      :rules="formRules"
      class="mail-service-page-edit-modal__form"
      @submit.native.prevent="handleEditEmail"
    >
      <ElAlert
        v-if="showSelectAtLeastOneNotificationError"
        class="mail-service-page-edit-modal__notification-min-error"
        :closable="false"
        type="error"
        show-icon
      >
        {{ $t('ADVERTISEMENT.MAIL_SERVICE.NOTIFICATION_MIN_ERROR') }}
      </ElAlert>

      <ElFormItem
        label-width="12.5rem"
        class="mail-service-page-edit-modal__form-item mail-service-page-edit-modal__form-item--separated"
        :label="$t('ADVERTISEMENT.MAIL_SERVICE.GENERAL_NOTIFICATIONS')"
      >
        <ElCheckbox
          v-model="formModel.monthlyNotification"
          :label="mailNotificationTypes.NEWSLETTER"
        >
          {{ $t('ADVERTISEMENT.MAIL_SERVICE.GENERAL_MONTHLY_NOTIFICATION') }}
        </ElCheckbox>
      </ElFormItem>
      <ElFormItem
        label-width="12.5rem"
        class="mail-service-page-edit-modal__form-item mail-service-page-edit-modal__form-item--separated"
        :label="$t('ADVERTISEMENT.MAIL_SERVICE.FINANCIAL_NOTIFICATIONS')"
      >
        <ElCheckboxGroup v-model="formModel.financialNotifications">
          <ElCheckbox :label="mailNotificationTypes.LOW_BUDGET">
            <i18n
              path="ADVERTISEMENT.MAIL_SERVICE.FINANCIAL_END_BUDGET_NOTIFICATION"
            >
              <ElInput
                v-model.number="formModel.lowBudgetThreshold"
                size="mini"
                class="mail-service-page-edit-modal__end-budget-threshold"
                type="number"
                dir="ltr"
              />
            </i18n>
          </ElCheckbox>
          <ElCheckbox :label="mailNotificationTypes.DAILY_STATS">
            {{
              $t('ADVERTISEMENT.MAIL_SERVICE.FINANCIAL_NIGHTLY_NOTIFICATION')
            }}
          </ElCheckbox>
        </ElCheckboxGroup>
      </ElFormItem>
      <ElFormItem
        label-width="12.5rem"
        class="mail-service-page-edit-modal__form-item mail-service-page-edit-modal__form-item--separated"
        :label="
          $t('ADVERTISEMENT.MAIL_SERVICE.CAMPAIGN_MANAGEMENT_NOTIFICATIONS')
        "
      >
        <ElCheckboxGroup v-model="formModel.campaignRelatedNotifications">
          <ElCheckbox :label="mailNotificationTypes.CAMPAIGNS_STOP_ALERTS">
            {{ $t('ADVERTISEMENT.MAIL_SERVICE.CAMPAIGN_STOP_ALERTS') }}
          </ElCheckbox>
          <ElCheckbox :label="mailNotificationTypes.KEYWORD_ALERTS">
            {{ $t('ADVERTISEMENT.MAIL_SERVICE.CAMPAIGN_KEYWORD_RELATED') }}
          </ElCheckbox>
        </ElCheckboxGroup>
      </ElFormItem>
      <ElFormItem
        label-width="28.75rem"
        class="mail-service-page-edit-modal__form-item mail-service-page-edit-modal__form-item--email"
        required
        :label="$t('ADVERTISEMENT.MAIL_SERVICE.TO_BE_SENT_EMAIL_TITLE')"
      >
        <ElInput disabled :value="formModel.email" type="email" />
      </ElFormItem>
    </ElForm>
    <div slot="footer" class="mail-service-page-edit-modal__footer">
      <ElButton
        type="primary"
        native-type="submit"
        size="large"
        :loading="isLoading"
        @click="handleEditEmail"
      >
        {{ $t('EDIT') }}
      </ElButton>
      <ElButton type="text" size="large" @click="closeAndReset">
        {{ $t('CANCEL') }}
      </ElButton>
    </div>
  </ElDialog>
</template>

<script>
import { mapActions } from 'vuex';
import mailNotificationTypes from '../../../../../common/constants/mailNotificationTypes';
import transformToServerNotificationTypes from '../../../../../common/transformers/transformToServerNotificationTypes';
import transformToClientNotificationTypes from './transformToClientNotificationTypes';
import required from '@/utils/validation-rules/required';
import emailValidation from '@/utils/validation-rules/email';
import form from '@/mixins/form';
import { EDIT_MAIL_SERVICE } from '@/store/types';
import { AD_MAIL_SERVICE_EDIT_MODAL_BUTTON } from '@/constants/gtm-events';

export default {
  name: 'MailServicePageEditModal',
  mixins: [form],
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    notificationProperties: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      formModel: {
        monthlyNotification: false,
        financialNotifications: [],
        campaignRelatedNotifications: [],
        lowBudgetThreshold: 3_000_000,
      },
      isLoading: false,
      showSelectAtLeastOneNotificationError: false,
    };
  },
  computed: {
    mailNotificationTypes() {
      return mailNotificationTypes;
    },
    formRules() {
      return {
        email: [
          required(this.$t('VALIDATION_REQUIRED_ERR')),
          emailValidation(),
        ],
      };
    },
    formRef() {
      return 'addMailForm';
    },
    areAnyNotificationSelected() {
      return (
        this.formModel.monthlyNotification ||
        this.formModel.financialNotifications.length ||
        this.formModel.campaignRelatedNotifications.length
      );
    },
  },
  watch: {
    notificationProperties: {
      handler({ lowBudgetThreshold, ...notif }) {
        const {
          monthlyNotification,
          financialNotifications,
          campaignRelatedNotifications,
        } = transformToClientNotificationTypes(notif);

        this.formModel.email = notif.email;
        this.formModel.monthlyNotification = monthlyNotification;
        this.formModel.financialNotifications = financialNotifications;
        this.formModel.campaignRelatedNotifications = campaignRelatedNotifications;
        this.formModel.lowBudgetThreshold = lowBudgetThreshold;
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      editEmail: EDIT_MAIL_SERVICE,
    }),
    validateNotificationMinSelection() {
      if (!this.areAnyNotificationSelected) {
        this.showSelectAtLeastOneNotificationError = true;
        return false;
      }
      this.showSelectAtLeastOneNotificationError = false;
      return true;
    },
    closeAndReset() {
      this.resetForm(this.formRef);

      this.close();
    },
    close() {
      this.$emit('input', false);
    },
    handleEditEmail() {
      if (
        !this.isValid(this.formRef) ||
        !this.validateNotificationMinSelection()
      )
        return;

      this.$gtm.trackEvent(AD_MAIL_SERVICE_EDIT_MODAL_BUTTON);

      this.isLoading = true;

      const transformedNotificationTypes = transformToServerNotificationTypes({
        monthlyNotification: this.formModel.monthlyNotification,
        financialNotifications: this.formModel.financialNotifications,
        campaignRelatedNotifications: this.formModel
          .campaignRelatedNotifications,
      });

      return this.editEmail({
        ...transformedNotificationTypes,
        id: this.notificationProperties.id,
        email: this.formModel.email,
        lowBudgetThreshold: this.formModel.lowBudgetThreshold,
      })
        .then(message => {
          this.$message.success(message);

          this.close();
        })
        .catch(({ message }) => {
          this.$message.error(message);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss">
.mail-service-page-edit-modal {
  &__notification-min-error {
    margin-bottom: $global-margin * 2;
  }

  &__header {
    font-size: $--font-size-large;

    &-desc {
      margin-top: $global-margin / 2;
      font-size: $lb-small-font-size;
      color: $lb-dark-gray;
    }
  }

  &__end-budget-threshold {
    width: rem-calc(100px);

    /* Firefox */
    input[type='number'] {
      /* stylelint-disable-next-line */
      -moz-appearance: textfield;
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      /* stylelint-disable-next-line */
      -webkit-appearance: none;
      margin: 0;
    }
  }

  &__form {
    &-item {
      .el-form-item__content {
        margin-left: 0 !important;
        // 12.5rem which is declared inside template
        margin-right: 12.5rem;
      }

      .el-form-item__label {
        height: auto;
        line-height: initial;
      }

      .el-checkbox-group {
        display: flex;
        flex-direction: column;
      }

      .el-checkbox {
        margin-left: 0 !important;
        margin-right: 0 !important;

        &:not(:first-child) {
          margin-top: $global-margin;
        }

        &__label {
          vertical-align: middle;
          white-space: normal !important;
        }
      }

      &--separated {
        border-bottom: 1px solid $lb-light-gray;
        padding-bottom: $global-padding * 1.5;
      }

      &--email {
        border: 1px solid $lb-light-gray;
        padding: $global-padding * 1.5;
        border-radius: $lb-border-radius * 2.5;

        .el-form-item__content {
          // 28.75rem which is declared inside template
          margin-right: rem-calc(460px);
        }
      }
    }
  }

  &__footer {
    display: flex;
    flex-direction: row-reverse;

    .el-button {
      &:not(:first-child) {
        margin-right: 0;
        margin-left: $global-margin;
      }
    }
  }
}
</style>
